import { Link } from "react-router-dom";
import { SortableHeader } from "@/components/shared/SortableHeader";
import { resolveInvoiceCustomerLabel, type InvoiceWithProject } from "@/hooks/use-invoices";
import { formatDate, formatNOK } from "@/lib/format";
import { cn } from "@/lib/utils";
import { TONE_TEXT } from "@/lib/widget-status";
import {
  daysOverdue,
  invoiceDisplayStatus,
  type InvoiceSortDir,
  type InvoiceSortKey,
} from "@/lib/invoice-list";
import { InvoiceStatusPill } from "./InvoiceStatusPill";
import { InvoiceSendError } from "./InvoiceSendError";
import { InvoiceTypeCell } from "./InvoiceTypeCell";
import { InvoicePdfButton } from "./InvoicePdfButton";

interface InvoiceTableProps {
  rows: InvoiceWithProject[];
  /** Antall og sum for hele utvalget, ikke bare siden. */
  total: { count: number; amount: number };
  today: string;
  /** Fakturaer som er gjort opp av kreditnota. */
  credited: ReadonlySet<string>;
  sort: InvoiceSortKey;
  dir: InvoiceSortDir;
  onSort: (key: InvoiceSortKey) => void;
  invoiceNoById: Map<string, number | null>;
  openingPdfId: string | null;
  onOpenPdf: (invoiceId: string) => void;
  emptyText: string;
}

const th = "py-3 px-2 text-xs uppercase tracking-wider";
const plainTh = cn(th, "text-left font-medium text-muted-foreground");

/** Fakturatabellen på /faktura fra sm og opp (VIB-2948). */
export function InvoiceTable({
  rows,
  total,
  today,
  credited,
  sort,
  dir,
  onSort,
  invoiceNoById,
  openingPdfId,
  onOpenPdf,
  emptyText,
}: InvoiceTableProps) {
  const header = (key: InvoiceSortKey, label: string, align?: "right") => (
    <SortableHeader label={label} active={sort === key} dir={dir} onToggle={() => onSort(key)} align={align} className={th} />
  );

  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm min-w-[960px]">
        <thead>
          <tr className="border-b-[1.5px] border-foreground">
            <th className={plainTh}>Fakturanr</th>
            {header("invoice_date", "Fakturadato")}
            {header("project", "Prosjekt")}
            {header("customer", "Kunde")}
            <th className={plainTh}>Type</th>
            {header("amount", "Beløp", "right")}
            {header("due_date", "Forfall")}
            <th className={plainTh}>Status</th>
            <th className="w-10"><span className="sr-only">PDF</span></th>
          </tr>
        </thead>
        <tbody>
          {rows.length === 0 && (
            <tr>
              <td colSpan={9} className="py-8 text-center text-muted-foreground">{emptyText}</td>
            </tr>
          )}
          {rows.map((inv) => {
            const status = invoiceDisplayStatus(inv, today, credited);
            return (
              <tr key={inv.id} className="border-b hover:bg-muted/30 align-middle">
                <td className="py-3 px-2 tabular-nums">{inv.invoice_no ?? <span className="text-muted-foreground">—</span>}</td>
                <td
                  className={cn("py-3 px-2 tabular-nums", !inv.invoice_date && "text-muted-foreground")}
                  title={inv.invoice_date ? undefined : "PowerOffice har ikke oppgitt fakturadato"}
                >
                  {formatDate(inv.invoice_date)}
                </td>
                <td className="py-3 px-2">
                  {inv.project ? (
                    <>
                      <Link to={`/prosjekter/${inv.project.id}`} className="text-primary font-medium hover:underline">
                        {inv.project.project_code || inv.project.name}
                      </Link>
                      {inv.project.project_code && (
                        <span className="block text-xs text-muted-foreground">{inv.project.name}</span>
                      )}
                    </>
                  ) : (
                    <span className="text-muted-foreground">—</span>
                  )}
                </td>
                <td className="py-3 px-2">{resolveInvoiceCustomerLabel(inv)}</td>
                <td className="py-3 px-2"><InvoiceTypeCell invoice={inv} invoiceNoById={invoiceNoById} /></td>
                <td className={cn("py-3 px-2 text-right tabular-nums whitespace-nowrap", inv.amount < 0 && TONE_TEXT.miss)}>
                  {formatNOK(inv.amount)}
                </td>
                <td className={cn("py-3 px-2 tabular-nums", status === "overdue" ? TONE_TEXT.miss : !inv.due_date && "text-muted-foreground")}>
                  {status === "credit_note" ? "—" : formatDate(inv.due_date)}
                </td>
                <td className="py-3 px-2">
                  <InvoiceStatusPill status={status} daysOverdue={daysOverdue(inv, today)} paidDate={inv.paid_date} />
                  {status === "draft" && inv.last_send_error && <InvoiceSendError error={inv.last_send_error} />}
                </td>
                <td className="py-3 px-2">
                  <InvoicePdfButton invoice={inv} opening={openingPdfId === inv.id} onOpen={onOpenPdf} />
                </td>
              </tr>
            );
          })}
        </tbody>
        {total.count > 0 && (
          <tfoot>
            <tr className="border-t-[1.5px] border-foreground font-semibold">
              <td colSpan={5} className="py-3 px-2">{total.count} {total.count === 1 ? "faktura" : "fakturaer"} i utvalget</td>
              <td className="py-3 px-2 text-right tabular-nums whitespace-nowrap">{formatNOK(total.amount)}</td>
              <td colSpan={3} className="py-3 px-2 font-normal text-muted-foreground">inkl. mva</td>
            </tr>
          </tfoot>
        )}
      </table>
    </div>
  );
}
